import { useMemo, useState } from "react";
import {
  GAP_LIST_MAX_PER_MUSCLE,
  modelFitGaps,
  muscleName,
  roundTypeAt,
  resolveExerciseMuscleCredits,
  simulateProgram,
  type CatalogMuscleSlug,
  type Exercise,
  type Locale,
  type ModelExtra,
  type SimulatedMuscle,
} from "@heraion/shared";

import { followPreviewProgram } from "../Library/modelCatalog";
import type { WizardRow } from "./ModelWizardDay";
import type { ModelWizardFit } from "./useModelWizardFit";

/** The exercise suggested for a muscle under MEV, and where it goes:
 *  `fits` false, the day with the most time left cannot take it; `extra`
 *  null, no day may take one (only deload days run). */
export type GapOffer = { slug: string; extra: ModelExtra | null; fits: boolean };

/** A muscle under MEV with what can close it. `offer` null: nothing to
 *  suggest (`noBodyweight` says when the filter is why). `allWeeks`: under
 *  MEV in every round the simulator judges (deloads out). */
export type GapRow = {
  muscle: SimulatedMuscle;
  days: number;
  allWeeks: boolean;
  offer: GapOffer | null;
  noBodyweight: boolean;
};

type WizardDay = { id: string; name: string; rows: readonly WizardRow[] };

/** A day's own credit for a muscle: a primary mover earns a full set. */
const DIRECT_CREDIT = 1;

// #492 F3: the wizard's program against the RP band (modelFitGaps on its
// simulation). Under MEV: one exercise and its day (placeModelExtra); over
// MRV: said only. `cutNotes`: per cut row, the muscles under MEV it trains.
export function useModelWizardGaps(
  fitted: ModelWizardFit,
  ctx: {
    catalog: ReadonlyArray<Exercise> | undefined;
    bySlug: ReadonlyMap<string, Exercise>;
    days: readonly WizardDay[];
    lang: Locale;
    /** Off in follow mode: no day list there to add to. */
    enabled: boolean;
  },
) {
  const { catalog, bySlug, days, lang, enabled } = ctx;
  const [bodyweightOnly, setBodyweightOnly] = useState(false);
  const { build, place, cut, settled } = fitted;
  // The last add, said until the next change but an add.
  const [added, setAdded] = useState<{ text: string; settled: object; bodyweightOnly: boolean } | null>(null);
  const run = useMemo(() => {
    if (!enabled || !catalog) return null;
    const built = build();
    if (!built.ok) return null;
    const program = followPreviewProgram(built.program, "model-wizard");
    const sim = simulateProgram(program, catalog);
    const judged = Array.from({ length: sim.cycleWeeks }, (_, w) => w).filter((w) => roundTypeAt(program, w) !== "deload").length;
    return { sim, judged };
  }, [enabled, catalog, build]);
  const sim = run?.sim ?? null;
  const gaps = useMemo(
    () => (sim && catalog ? modelFitGaps(sim, catalog, { bodyweightOnly, maxPerMuscle: GAP_LIST_MAX_PER_MUSCLE }) : null),
    [sim, catalog, bodyweightOnly],
  );
  const freq = useMemo(() => new Map(sim?.frequency.muscles.map((f) => [f.key, f.daysPerWeek]) ?? []), [sim]);
  const below = useMemo<GapRow[]>(() => {
    if (!sim || !gaps) return [];
    const skipped = new Set(gaps.noBodyweight);
    return gaps.belowMev.flatMap((g): GapRow[] => {
      const muscle = sim.muscles.find((m) => m.muscle === g.muscle);
      if (!muscle) return [];
      const rec = gaps.recommendations.find((r) => r.muscle === g.muscle);
      // The first candidate the model can write and the day lacks: one trial fit.
      let offer: GapOffer | null = null;
      for (const c of rec?.candidates ?? []) {
        const at = place(c.slug);
        if (at.ok) offer = { slug: c.slug, extra: at.extra, fits: true };
        else if (at.reason === "no-room") offer = { slug: c.slug, extra: at.dayId === null ? null : { slug: c.slug, dayId: at.dayId }, fits: false };
        if (offer) break;
      }
      const allWeeks = (run?.judged ?? 0) > 0 && muscle.weeksBelowMev.length === run?.judged;
      return [{ muscle, days: freq.get(g.muscle) ?? 0, allWeeks, offer, noBodyweight: skipped.has(g.muscle) }];
    });
  }, [sim, run, gaps, place, freq]);
  const above = useMemo(() => {
    if (!sim || !gaps) return [];
    return gaps.aboveMrv.flatMap((f) => {
      const muscle = sim.muscles.find((m) => m.muscle === f.muscle);
      return muscle ? [{ muscle, days: freq.get(f.muscle) ?? 0 }] : [];
    });
  }, [sim, gaps, freq]);
  const cutNotes = useMemo(() => {
    const under = new Set<CatalogMuscleSlug>(gaps?.belowMev.map((g) => g.muscle) ?? []);
    const notes = new Map<string, string[]>();
    for (const row of days.flatMap((d) => d.rows)) {
      if (!cut.has(row.id)) continue;
      const ex = bySlug.get(row.exercise);
      const direct = ex ? resolveExerciseMuscleCredits(ex).filter(([m, c]) => c >= DIRECT_CREDIT && under.has(m)) : [];
      notes.set(row.id, direct.map(([m]) => muscleName(m, lang)));
    }
    return notes;
  }, [gaps, days, cut, bySlug, lang]);
  const dayName = (id: string) => days.find((d) => d.id === id)?.name ?? "";
  const noBodyweight = gaps?.noBodyweight ?? [];
  const addedNow = added?.settled === settled && added.bodyweightOnly === bodyweightOnly ? added.text : null;
  const say = (text: string) => setAdded({ text, settled, bodyweightOnly });
  return { ready: sim !== null, below, above, cutNotes, dayName, bodyweightOnly, setBodyweightOnly, noBodyweight, added: addedNow, say };
}

export type ModelWizardGapsState = ReturnType<typeof useModelWizardGaps>;
